// La hoja de Mostrar en uso / Plano técnico (spec §3.4): la
// miniatura de la prenda, la instrucción adicional (no en Plano) y, en el pie fijo,
// cuántas imágenes y Generar (ADR-2026-09-17-las-hojas-del-diseno-eligen-la-cantidad).

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import CachedImg from '../../../../../components/CachedImg';
import { CreditBadge } from '../../../../../components/CreditBadge';
import { HojaLateral } from '../../../../../chat-atlas/ui/HojaLateral';
import { ContadorImagenes } from '../../../components/ContadorImagenes';
import { useDiseno, type PanelAbierto } from '../disenoContext';
import { ACCIONES_CON_CANTIDAD, CANTIDAD_POR_ACCION, MAX_CANTIDAD, TOPE_INSTRUCCION } from '../promptsDeDiseno';
import { palabraDeModeloActual } from '../../../../../lib/nombreDeModelo';

const TEXTOS: Record<Exclude<PanelAbierto['accion'], 'mezcla'>, { titulo: string; bajada: string; ayuda: string }> = {
  'en-uso': {
    titulo: 'Mostrar en uso',
    bajada: 'Fotos de una persona real usando esta prenda: cada una con su escena, su plano y su pose.',
    get ayuda() {
      return `Ej.: "con jean claro, en la calle, luz de tarde", "${palabraDeModeloActual().singular} masculino de 30 años".`;
    },
  },
  plano: {
    titulo: 'Plano técnico',
    bajada: 'Frente y espalda, en línea, sobre blanco.',
    ayuda: '',
  },
};

export const PanelDeAccion: React.FC<{ panel: PanelAbierto }> = ({ panel }) => {
  const d = useDiseno();
  const accion = panel.accion as keyof typeof TEXTOS;
  const t = TEXTOS[accion];
  const [instruccion, setInstruccion] = useState('');
  const [cantidad, setCantidad] = useState(CANTIDAD_POR_ACCION[accion]);
  const [enviando, setEnviando] = useState(false);
  const eligeCantidad = ACCIONES_CON_CANTIDAD.includes(accion);
  const url = d.urlDe(panel.celdaId);

  const generar = async () => {
    if (enviando) return;
    setEnviando(true);
    const r = await d.lanzar(accion, panel.celdaId, { instruccion, cantidad });
    setEnviando(false);
    if (!r.ok) {
      d.avisarError(r.motivo);
      return;
    }
    d.abrirPanel(null);
  };

  const pie = (
    <div className="flex items-center gap-3">
      {eligeCantidad && (
        <div className="shrink-0 rounded-xl bg-black/[0.035] px-2 py-1 [&>span]:mt-0">
          <ContadorImagenes valor={cantidad} max={MAX_CANTIDAD} onChange={setCantidad} />
        </div>
      )}
      <div className="flex flex-1 justify-end gap-2">
        <button type="button" onClick={() => d.abrirPanel(null)} className="px-3 py-2 text-[15px] text-[#3C3C43]/60 transition-colors hover:text-[rgb(var(--brand-black-rgb)_/_0.8)]">
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => void generar()}
          disabled={enviando}
          className="inline-flex items-center gap-2 rounded-md bg-brand-primary px-4 py-2 text-[15px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:opacity-60"
        >
          {enviando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Generar
          <CreditBadge credits={cantidad} tone="on-primary" size="sm" />
        </button>
      </div>
    </div>
  );

  return (
    <HojaLateral abierta titulo={t.titulo} bajada={t.bajada} onCerrar={() => d.abrirPanel(null)} pie={pie}>
      <div className="flex flex-col gap-4 px-2 py-1">
        <div className="mx-auto aspect-square w-40 overflow-hidden rounded-2xl bg-[#F3F4F8]">
          {url && <CachedImg src={url} alt="" className="h-full w-full object-cover" />}
        </div>
        <p className="text-center text-[13px] text-[#3C3C43]/60">{d.tituloDe(panel.celdaId)}</p>
        {accion !== 'plano' && (
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-normal uppercase tracking-wide text-[#3C3C43]/60">Instrucción opcional</span>
            <textarea
              value={instruccion}
              onChange={(e) => setInstruccion(e.target.value.slice(0, TOPE_INSTRUCCION))}
              placeholder={t.ayuda}
              rows={4}
              className="w-full resize-none rounded-xl border border-black/[0.08] bg-white px-3 py-2 text-[14px] text-brand-black outline-none focus:border-brand-primary"
            />
          </label>
        )}
      </div>
    </HojaLateral>
  );
};
